import classnames from 'classnames';
import React, { KeyboardEvent } from 'react';
import { FormControl, FormGroup } from 'react-bootstrap';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormGetValues,
  UseFormRegister,
  UseFormTrigger,
  useWatch,
} from 'react-hook-form';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import checkIconFilledGreen from 'v2/icons/checkIconFilledGreen.svg';
import crossIconFilled from 'v2/icons/crossIconFilledRed.svg';
import { CreateSearchFormData } from '../CreateSearchSchema';
import styles from './questionsStep.module.scss';

interface QuestionsStepProps {
  control: Control<CreateSearchFormData>;
  register: UseFormRegister<CreateSearchFormData>;
  errors: FieldErrors<CreateSearchFormData>;
  trigger: UseFormTrigger<CreateSearchFormData>;
  getValues: UseFormGetValues<CreateSearchFormData>;
}

const QuestionsStep = ({
  control,
  register,
  errors,
  trigger,
  getValues,
}: QuestionsStepProps) => {
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'post_questions',
  });

  const questions = useWatch({
    control,
    name: 'post_questions',
    defaultValue: getValues('post_questions'),
  });

  const addNewQuestion = (index: number) => {
    if (!fields[index + 1] && fields.length < 10) {
      append({ value: '' });
    }
  };

  return (
    <div className={classnames('px-3', styles.root)}>
      <p className="text-light8">
        Please specify as many custom questions below as necessary. All data
        providers responding to your request will be required to answer them.
      </p>
      <div>
        <ul className={styles.listContainer}>
          {fields.map((item, index) => {
            const { onChange, ...registerObj } = register(
              `post_questions.${index}.value` as `post_questions.${number}.value`
            );
            return (
              <li key={item.id} className="position-relative">
                {questions[index]?.value ? (
                  <img
                    src={checkIconFilledGreen}
                    className={styles.checkIcon}
                    alt="questionOk"
                  />
                ) : null}
                <FormGroup key={index}>
                  <FormLabel
                    label={`Question ${index + 1}`}
                    required={false}
                    error={
                      // Need to parse the error object here because we have a custom validation for post_questions
                      // and the error object received is after validating the array, not each field in the array.
                      (
                        errors.post_questions?.[index] as {
                          message: string;
                        }
                      )?.message
                    }
                  />
                  <div className="d-flex algin-items-center">
                    <FormControl
                      className={styles.inputText}
                      type="text"
                      {...registerObj}
                      onChange={(e) => {
                        onChange(e);
                        trigger();
                      }}
                      onFocus={() => addNewQuestion(index)}
                      disabled={
                        index > 0 &&
                        !questions[index - 1]?.value &&
                        !questions[index + 1]
                      }
                      data-cy={`CreateRequest:request-question-${index}`}
                      autoComplete="off"
                      placeholder="Type your question here..."
                      aria-label={registerObj.name}
                      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // To don't add complex logic with refs, I'm using
                          // javascript API to focus next element when uer hits
                          // enter key
                          const el = document.querySelector<HTMLInputElement>(
                            `input[name='post_questions.${index + 1}.value']`
                          );
                          if (el) {
                            el.focus();
                          }
                        }
                      }}
                    />

                    {questions.length > 1 ? (
                      <button
                        tabIndex={-1}
                        className={classnames('bg-white p-0 ml-2')}
                        onClick={() => {
                          remove(index);
                          trigger();
                        }}
                        type="button"
                      >
                        <img src={crossIconFilled} alt="deleteQuestion" />
                      </button>
                    ) : (
                      <div className={styles.removeButtonPlaceholder} />
                    )}
                  </div>
                </FormGroup>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default QuestionsStep;
